import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';

import { Alert, Button, TextField } from '@shared';

import { createPortalApi } from '@/services/api';
import { errorKeyOf, type ApiErrorKey } from '@/services/api/errors';
import type { RegistrationApi } from '@/services/api/registration-api';
import { useTranslations } from '@/i18n/client';
import { PasswordField } from './PasswordField';
import {
  MIN_PASSWORD_LENGTH,
  PASSWORD_PATTERN,
  PASSWORD_REQUIREMENTS,
  SPECIAL_CHARS_HINT,
} from './password-policy';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface RegisterFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export interface RegisterFormProps {
  /** Injected registration API — tests supply a double for the network boundary. */
  registration?: RegistrationApi;
  onSuccess?: () => void;
}

export function RegisterForm({ registration, onSuccess }: RegisterFormProps) {
  const t = useTranslations('auth.register');
  const tStatus = useTranslations('common.status');
  const tErrors = useTranslations('errors.api');

  const [api] = useState(() => registration ?? createPortalApi().registration);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<ApiErrorKey | null>(null);
  const [succeeded, setSucceeded] = useState(false);
  const [passwordHintsVisible, setPasswordHintsVisible] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    control,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const password = useWatch({ control, name: 'password' }) ?? '';
  const passwordField = register('password', {
    required: t('errors.passwordRequired'),
    minLength: { value: MIN_PASSWORD_LENGTH, message: t('errors.passwordTooShort') },
    pattern: { value: PASSWORD_PATTERN, message: t('errors.passwordRequirementsNotMet') },
  });

  async function onSubmit(values: RegisterFormValues): Promise<void> {
    setSubmitting(true);
    setFailure(null);
    try {
      await api.register({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        newPassword: values.password,
      });
      setSucceeded(true);
      onSuccess?.();
    } catch (error) {
      setFailure(errorKeyOf(error));
    } finally {
      setSubmitting(false);
    }
  }

  if (succeeded) {
    return (
      <Alert variant="success">
        <strong>{t('successTitle')}</strong>
        <p>{t('successMessage')}</p>
      </Alert>
    );
  }

  return (
    <form className="md-form" onSubmit={handleSubmit(onSubmit)} noValidate>
      {failure ? <Alert variant="error">{tErrors(failure)}</Alert> : null}

      <TextField
        label={t('firstNameLabel')}
        autoComplete="given-name"
        invalid={Boolean(errors.firstName)}
        {...(errors.firstName?.message ? { message: errors.firstName.message } : {})}
        {...register('firstName', { required: t('errors.firstNameRequired') })}
      />

      <TextField
        label={t('lastNameLabel')}
        autoComplete="family-name"
        invalid={Boolean(errors.lastName)}
        {...(errors.lastName?.message ? { message: errors.lastName.message } : {})}
        {...register('lastName', { required: t('errors.lastNameRequired') })}
      />

      <TextField
        label={t('emailLabel')}
        type="email"
        autoComplete="email"
        invalid={Boolean(errors.email)}
        {...(errors.email?.message ? { message: errors.email.message } : {})}
        {...register('email', {
          required: t('errors.emailRequired'),
          pattern: { value: EMAIL_PATTERN, message: t('errors.emailInvalid') },
        })}
      />

      <PasswordField
        label={t('passwordLabel')}
        autoComplete="new-password"
        invalid={Boolean(errors.password)}
        showLabel={t('showPasswordLabel')}
        hideLabel={t('hidePasswordLabel')}
        {...(errors.password?.message ? { message: errors.password.message } : {})}
        {...passwordField}
        onFocus={() => setPasswordHintsVisible(true)}
        onBlur={(event) => {
          void passwordField.onBlur(event);
          setPasswordHintsVisible(false);
        }}
      />

      {passwordHintsVisible ? (
        <ul className="md-password-hints" aria-label={t('passwordRequirementsLabel')}>
          {PASSWORD_REQUIREMENTS.map((requirement) => {
            const fulfilled = requirement.test(password);
            return (
              <li key={requirement.key} className="md-password-hints__item" data-fulfilled={fulfilled}>
                {fulfilled ? (
                  <CheckCircleIcon
                    className="md-password-hints__icon md-password-hints__icon--ok"
                    aria-hidden="true"
                  />
                ) : (
                  <XCircleIcon
                    className="md-password-hints__icon md-password-hints__icon--fail"
                    aria-hidden="true"
                  />
                )}
                <span>
                  {t(`passwordRequirements.${requirement.key}`)}
                  {requirement.key === 'special' ? ` ${SPECIAL_CHARS_HINT}` : ''}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      <PasswordField
        label={t('confirmPasswordLabel')}
        autoComplete="new-password"
        invalid={Boolean(errors.confirmPassword)}
        showLabel={t('showPasswordLabel')}
        hideLabel={t('hidePasswordLabel')}
        {...(errors.confirmPassword?.message ? { message: errors.confirmPassword.message } : {})}
        {...register('confirmPassword', {
          required: t('errors.confirmPasswordRequired'),
          validate: (value) => value === getValues('password') || t('errors.passwordsDoNotMatch'),
        })}
      />

      <Button type="submit" className="max-sm:w-full" loading={submitting} loadingLabel={tStatus('working')}>
        {t('submitButton')}
      </Button>
    </form>
  );
}
